import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import MuiLink from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  getFacility,
  listQueueRecoveryReasons,
  replaceQueueRecoveryReasons,
  setQueueConfig,
  updateFacility,
  type FacilityDetail,
  type FacilityListItem,
  type QueueAutoCourtStrategy,
  type QueueAutoLateJoinTimingPolicy,
  type QueueRecoveryReason,
  type QueueRecoveryReasonInput,
} from "@/libs/facilityApi";
import { checkFacilityPermission } from "@/libs/permissionsApi";
import {
  DROP_IN_CHECK_IN_MODES,
  SELECTABLE_CHECK_IN_MODES,
  updateCheckInMode,
  type CheckInMode,
} from "@/libs/dropInApi";
import { listPricingRules, type PricingRule } from "@/libs/ratesApi";
import {
  SimpleRatesMode,
  OPEN_PLAY_SIMPLE_CONFIG,
} from "@/components/rates/simple-rates-mode";
import { OpenPlayCancellationPolicyCard } from "./open-play-cancellation-policy-card";

// SetupTab — the configuration half of the Drop-in service home (#143 slice 3).
// Four settings groups, all facility-scoped:
//   1. Check-in     — the check-in-MODE select + the staff-check-in SWITCH,
//                     co-located because they interlock: "Staff scans each player
//                     in" is only selectable once staff check-in is available. A
//                     single dirty-gated Save fires both PATCHes (check-in mode
//                     via dropInApi, staff availability via updateFacility).
//   2. Drop-in queue — assignment mode + the complete Auto assignment policy;
//                     court assignment consumes the facility's shared courts.
//   3. Cancellation — immutable preview-and-publish policy versions.
//   4. Drop-in pricing — the generalized SimpleRatesMode form pointed at the
//                     open_play service (per-session Standard/Peak/Weekend). An
//                     open_play rate also DEFINES the drop-in window (Slice 1 of
//                     the unified-pricing initiative).
//
// The FE owns NO business logic: the backend enforces the staff-check-in
// constraint, the queue-config bounds, and pricing —
// this tab forwards values and surfaces backend 4xx honestly. facility:manage
// (server-computed) gates the check-in + queue controls; the pricing form is
// facility:price-gated server-side and surfaces its own 403.

const MODE_LABELS: Record<CheckInMode, string> = {
  self_check_in: "Players check themselves in",
  staff_check_in: "Staff scans each player in",
  hybrid: "Hybrid (legacy)",
};

const DEFAULT_QUEUE_POLICY = {
  sessionDurationMinutes: 30,
  minimumRemainingMinutes: 20,
  autoCourtStrategy: "prefer_fresh" as QueueAutoCourtStrategy,
  autoTargetOccupancy: 4,
  autoMinimumStartingOccupancy: 2,
  autoLateJoinTimingPolicy:
    "inherit_remaining" as QueueAutoLateJoinTimingPolicy,
  autoMaxWaitMinutes: 0,
};

const AUTO_STRATEGY_LABELS: Record<QueueAutoCourtStrategy, string> = {
  fill_existing: "Fill existing courts first",
  prefer_fresh: "Prefer fresh courts",
  fresh_only: "Fresh courts only",
};

const AUTO_LATE_JOIN_TIMING_LABELS: Record<
  QueueAutoLateJoinTimingPolicy,
  string
> = {
  fresh_block: "Start a fresh court block",
  extend_existing: "Extend the active court block",
  inherit_remaining: "Keep the active court end time",
};

type RecoveryAction = QueueRecoveryReasonInput["actions"][number];

const RECOVERY_ACTIONS: ReadonlyArray<{
  value: RecoveryAction;
  label: string;
}> = [
  { value: "skip", label: "Skip" },
  { value: "remove", label: "Remove" },
  { value: "end_session", label: "End session" },
  { value: "reassign", label: "Reassign" },
  { value: "end_admission", label: "End admission" },
];

function editableRecoveryReasons(
  reasons: QueueRecoveryReason[],
): QueueRecoveryReasonInput[] {
  return reasons.map((reason) => ({
    reason_id: reason.reason_id,
    label: reason.label,
    actions: [...reason.actions],
  }));
}

function isCheckInMode(value: string | null | undefined): value is CheckInMode {
  return (DROP_IN_CHECK_IN_MODES as readonly string[]).includes(value ?? "");
}

function SettingsSection({
  children,
  title,
  titleId,
}: {
  children: ReactNode;
  title: string;
  titleId: string;
}) {
  return (
    <Paper
      aria-labelledby={titleId}
      component="section"
      data-slot="card"
      sx={{ overflow: "hidden" }}
      variant="outlined"
    >
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <Typography component="h2" id={titleId} variant="h6">
          {title}
        </Typography>
      </Box>
      <Divider />
      <Stack spacing={2} sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
        {children}
      </Stack>
    </Paper>
  );
}

type QueuePolicyField =
  | "duration"
  | "minimum"
  | "target"
  | "minimumStarting"
  | "maximumWait";

function QueuePolicyNumberInput({
  id,
  label,
  min,
  max,
  value,
  disabled,
  errorId,
  help,
  onChange,
  onDirty,
}: {
  id: string;
  label: string;
  min: number;
  max: number;
  value: number | null;
  disabled: boolean;
  errorId?: string;
  help?: string;
  onChange: (value: number | null) => void;
  onDirty: () => void;
}) {
  const helpId = help ? `${id}-help` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <TextField
      disabled={disabled}
      error={Boolean(errorId)}
      fullWidth
      helperText={help}
      id={id}
      label={label}
      onChange={(event) => {
        const parsed =
          event.target.value === "" ? null : Number(event.target.value);
        onChange(parsed != null && Number.isNaN(parsed) ? null : parsed);
        onDirty();
      }}
      size="small"
      slotProps={{
        formHelperText: { id: helpId },
        htmlInput: {
          "aria-describedby": describedBy,
          max,
          min,
          step: 1,
        },
      }}
      type="number"
      value={value ?? ""}
    />
  );
}

export interface SetupTabProps {
  facilityId: string;
}

export function SetupTab({ facilityId }: SetupTabProps) {
  const queryClient = useQueryClient();
  const aliveRef = useRef(true);
  const facilityScopeRef = useRef({ facilityId });
  if (facilityScopeRef.current.facilityId !== facilityId) {
    // A new object is a new generation, including A -> B -> A switches.
    facilityScopeRef.current = { facilityId };
  }
  const facilityScope = facilityScopeRef.current;
  const isCurrentScope = useCallback(
    (scope: { facilityId: string }) =>
      aliveRef.current && facilityScopeRef.current === scope,
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedFacilityScope, setLoadedFacilityScope] = useState<{
    facilityId: string;
  } | null>(null);
  const facilityDetailLoaded = loadedFacilityScope === facilityScope;

  // Permission gate (server-computed facility:manage) — defaults to false
  // (locked) until it resolves and on any error. permissionChecked keeps the
  // lock-reason copy from flashing during the pending window.
  const [canManage, setCanManage] = useState(false);
  const [permissionChecked, setPermissionChecked] = useState(false);

  // Check-in card — mode + staff availability. Each tracks its loaded baseline so
  // a single Save is dirty-gated and PATCHes only what changed.
  const [checkInMode, setCheckInMode] = useState<CheckInMode>("self_check_in");
  const [loadedCheckInMode, setLoadedCheckInMode] =
    useState<CheckInMode>("self_check_in");
  const [staffCheckInAvailable, setStaffCheckInAvailable] = useState(false);
  const [loadedStaffCheckInAvailable, setLoadedStaffCheckInAvailable] =
    useState(false);
  const [savingCheckIn, setSavingCheckIn] = useState(false);
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [checkInNotice, setCheckInNotice] = useState<string | null>(null);

  // Drop-in queue card — one atomic assignment-policy write.
  const [queueAssignmentMode, setQueueAssignmentMode] = useState<
    "manual" | "auto" | "self_serve"
  >("auto");
  const [queueSessionDuration, setQueueSessionDuration] = useState<
    number | null
  >(DEFAULT_QUEUE_POLICY.sessionDurationMinutes);
  const [queueMinimumRemaining, setQueueMinimumRemaining] = useState<
    number | null
  >(DEFAULT_QUEUE_POLICY.minimumRemainingMinutes);
  const [queueAutoCourtStrategy, setQueueAutoCourtStrategy] =
    useState<QueueAutoCourtStrategy>(DEFAULT_QUEUE_POLICY.autoCourtStrategy);
  const [queueAutoTargetOccupancy, setQueueAutoTargetOccupancy] = useState<
    number | null
  >(DEFAULT_QUEUE_POLICY.autoTargetOccupancy);
  const [
    queueAutoMinimumStartingOccupancy,
    setQueueAutoMinimumStartingOccupancy,
  ] = useState<number | null>(
    DEFAULT_QUEUE_POLICY.autoMinimumStartingOccupancy,
  );
  const [queueAutoLateJoinTimingPolicy, setQueueAutoLateJoinTimingPolicy] =
    useState<QueueAutoLateJoinTimingPolicy>(
      DEFAULT_QUEUE_POLICY.autoLateJoinTimingPolicy,
    );
  const [queueAutoMaxWait, setQueueAutoMaxWait] = useState<number | null>(
    DEFAULT_QUEUE_POLICY.autoMaxWaitMinutes,
  );
  const [queuePolicyLoaded, setQueuePolicyLoaded] = useState(false);
  const [queueDirty, setQueueDirty] = useState(false);
  const [savingQueue, setSavingQueue] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [queueNotice, setQueueNotice] = useState<string | null>(null);
  const [queueRangeError, setQueueRangeError] = useState<string | null>(null);
  const [queueInvalidFields, setQueueInvalidFields] = useState<
    QueuePolicyField[]
  >([]);

  const [recoveryReasons, setRecoveryReasons] = useState<
    QueueRecoveryReasonInput[]
  >([]);
  const [recoveryDirty, setRecoveryDirty] = useState(false);
  const [savingRecovery, setSavingRecovery] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [recoveryNotice, setRecoveryNotice] = useState<string | null>(null);
  const [recoveryCatalogFacilityId, setRecoveryCatalogFacilityId] = useState<
    string | null
  >(null);
  const recoveryReasonsLoaded = recoveryCatalogFacilityId === facilityId;

  // Drop-in pricing card — the facility's pricing rules, filtered to open_play.
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [pricingLoaded, setPricingLoaded] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const openPlayRules = rules.filter((r) => r.service === "open_play");

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadScope = facilityScopeRef.current;
    setLoading(true);
    setError(null);
    setLoadedFacilityScope(null);
    // Mutation progress and feedback belong to one facility generation only.
    setSavingCheckIn(false);
    setCheckInError(null);
    setCheckInNotice(null);
    setSavingQueue(false);
    setQueuePolicyLoaded(false);
    setQueueDirty(false);
    setQueueError(null);
    setQueueNotice(null);
    setQueueRangeError(null);
    setQueueInvalidFields([]);
    setSavingRecovery(false);
    setRecoveryDirty(false);
    setRecoveryError(null);
    setRecoveryNotice(null);
    setRecoveryCatalogFacilityId(null);
    setPricingLoaded(false);
    setPricingError(null);
    (async () => {
      const [facilityResult, pricingResult, recoveryResult] =
        await Promise.allSettled([
          getFacility(facilityId),
          listPricingRules(facilityId),
          listQueueRecoveryReasons(facilityId),
        ]);
      if (cancelled || !isCurrentScope(loadScope)) return;

      if (facilityResult.status === "fulfilled") {
        seedFromDetail(facilityResult.value, loadScope);
      } else {
        const err = facilityResult.reason;
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load drop-in settings",
        );
      }

      if (pricingResult.status === "fulfilled") {
        setRules(pricingResult.value);
        setPricingLoaded(true);
        setPricingError(null);
      } else {
        setRules([]);
        setPricingLoaded(false);
        const err = pricingResult.reason;
        setPricingError(
          err instanceof Error ? err.message : "Failed to load drop-in pricing",
        );
      }

      if (recoveryResult.status === "fulfilled") {
        setRecoveryReasons(editableRecoveryReasons(recoveryResult.value));
        setRecoveryCatalogFacilityId(facilityId);
        setRecoveryError(null);
      } else {
        const err = recoveryResult.reason;
        setRecoveryReasons([]);
        setRecoveryCatalogFacilityId(null);
        setRecoveryError(
          err instanceof Error
            ? err.message
            : "Failed to load recovery reasons",
        );
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, isCurrentScope]);

  // Permission gate — server-computed facility:manage, never derived client-side.
  useEffect(() => {
    let cancelled = false;
    setCanManage(false);
    setPermissionChecked(false);
    Promise.resolve(checkFacilityPermission(facilityId, "facility:manage"))
      .then((res) => {
        if (cancelled) return;
        setCanManage(Boolean(res?.allowed));
      })
      .catch(() => {
        if (cancelled) return;
        setCanManage(false);
      })
      .finally(() => {
        if (cancelled) return;
        setPermissionChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  function seedFromDetail(
    detail: FacilityDetail,
    detailScope: { facilityId: string },
  ) {
    const mode = isCheckInMode(detail.drop_in_check_in_mode)
      ? detail.drop_in_check_in_mode
      : "self_check_in";
    setCheckInMode(mode);
    setLoadedCheckInMode(mode);
    const staffAvail = detail.staff_check_in_available ?? false;
    setStaffCheckInAvailable(staffAvail);
    setLoadedStaffCheckInAvailable(staffAvail);
    setCheckInError(null);
    setCheckInNotice(null);

    setQueueAssignmentMode(detail.queue_assignment_mode);
    setQueueSessionDuration(detail.queue_session_duration_minutes);
    setQueueMinimumRemaining(detail.queue_minimum_remaining_minutes);
    setQueueAutoCourtStrategy(detail.queue_auto_court_strategy);
    setQueueAutoTargetOccupancy(detail.queue_auto_target_occupancy);
    setQueueAutoMinimumStartingOccupancy(
      detail.queue_auto_minimum_starting_occupancy,
    );
    setQueueAutoLateJoinTimingPolicy(detail.queue_auto_late_join_timing_policy);
    setQueueAutoMaxWait(detail.queue_auto_max_wait_minutes);
    setQueuePolicyLoaded(true);
    setQueueDirty(false);
    setQueueError(null);
    setQueueNotice(null);
    setQueueRangeError(null);
    setQueueInvalidFields([]);
    setLoadedFacilityScope(detailScope);
  }

  const refreshScope = facilityScope;
  const refreshRules = useCallback(async () => {
    try {
      const next = await listPricingRules(refreshScope.facilityId);
      if (!isCurrentScope(refreshScope)) return;
      setRules(next);
      setPricingLoaded(true);
      setPricingError(null);
    } catch (err) {
      if (isCurrentScope(refreshScope)) {
        setPricingLoaded(false);
        setPricingError(
          err instanceof Error ? err.message : "Failed to load drop-in pricing",
        );
      }
      throw err;
    }
  }, [isCurrentScope, refreshScope]);

  // Turning staff availability OFF must also drop the mode off "staff_check_in"
  // (that combo is a backend 422) — keep the local intent valid before Save.
  const onToggleStaff = (on: boolean) => {
    setStaffCheckInAvailable(on);
    if (!on && checkInMode === "staff_check_in")
      setCheckInMode("self_check_in");
    setCheckInNotice(null);
  };

  const checkInDirty =
    checkInMode !== loadedCheckInMode ||
    staffCheckInAvailable !== loadedStaffCheckInAvailable;

  const clearQueueValidation = () => {
    setQueueRangeError(null);
    setQueueInvalidFields([]);
    setQueueError(null);
  };

  const saveCheckIn = async () => {
    if (!facilityDetailLoaded || !checkInDirty || savingCheckIn) return;
    const saveScope = facilityScopeRef.current;
    setSavingCheckIn(true);
    setCheckInError(null);
    setCheckInNotice(null);

    const modeChanged = checkInMode !== loadedCheckInMode;
    const availChanged = staffCheckInAvailable !== loadedStaffCheckInAvailable;
    const disabling = availChanged && !staffCheckInAvailable;

    const applyMode = async () => {
      if (modeChanged)
        await updateCheckInMode(saveScope.facilityId, checkInMode);
    };
    const applyAvail = async () => {
      if (availChanged)
        await updateFacility(saveScope.facilityId, {
          staff_check_in_available: staffCheckInAvailable,
        });
    };

    try {
      // Order matters for the backend constraint (staff mode requires availability):
      //   enabling / unchanged → availability first, then mode
      //   disabling            → mode off staff first, then availability
      if (disabling) {
        await applyMode();
        await applyAvail();
      } else {
        await applyAvail();
        await applyMode();
      }
      if (!isCurrentScope(saveScope)) return;
      setLoadedCheckInMode(checkInMode);
      setLoadedStaffCheckInAvailable(staffCheckInAvailable);
      setCheckInNotice("Check-in settings saved.");
    } catch (err) {
      if (!isCurrentScope(saveScope)) return;
      setCheckInError(
        err instanceof Error ? err.message : "Failed to save check-in settings",
      );
    } finally {
      if (isCurrentScope(saveScope)) setSavingCheckIn(false);
    }
  };

  // Client bounds mirror QueueConfigSet for immediate form feedback; the server
  // remains authoritative and persists the complete policy atomically.
  const saveQueueConfig = async () => {
    if (!facilityDetailLoaded || !queuePolicyLoaded || savingQueue) return;
    setQueueNotice(null);
    clearQueueValidation();
    if (
      queueSessionDuration == null ||
      !Number.isInteger(queueSessionDuration) ||
      queueSessionDuration < 2 ||
      queueSessionDuration > 720
    ) {
      setQueueRangeError(
        "Court block duration must be a whole number between 2 and 720 minutes.",
      );
      setQueueInvalidFields(["duration"]);
      return;
    }
    if (
      queueMinimumRemaining == null ||
      !Number.isInteger(queueMinimumRemaining) ||
      queueMinimumRemaining < 1 ||
      queueMinimumRemaining > 719
    ) {
      setQueueRangeError(
        "Minimum time to join an active court must be a whole number between 1 and 719 minutes.",
      );
      setQueueInvalidFields(["minimum"]);
      return;
    }
    if (queueMinimumRemaining >= queueSessionDuration) {
      setQueueRangeError(
        "Minimum time to join an active court must be shorter than the court block duration.",
      );
      setQueueInvalidFields(["duration", "minimum"]);
      return;
    }
    if (
      queueAutoTargetOccupancy == null ||
      !Number.isInteger(queueAutoTargetOccupancy) ||
      queueAutoTargetOccupancy < 2 ||
      queueAutoTargetOccupancy > 6
    ) {
      setQueueRangeError(
        "Target occupancy must be a whole number between 2 and 6 players.",
      );
      setQueueInvalidFields(["target"]);
      return;
    }
    if (
      queueAutoMinimumStartingOccupancy == null ||
      !Number.isInteger(queueAutoMinimumStartingOccupancy) ||
      queueAutoMinimumStartingOccupancy < 1 ||
      queueAutoMinimumStartingOccupancy > queueAutoTargetOccupancy
    ) {
      setQueueRangeError(
        "Minimum players to open a court must be a whole number between 1 and the target occupancy.",
      );
      setQueueInvalidFields(["minimumStarting", "target"]);
      return;
    }
    if (
      queueAutoMaxWait == null ||
      !Number.isInteger(queueAutoMaxWait) ||
      queueAutoMaxWait < 0 ||
      queueAutoMaxWait > 120
    ) {
      setQueueRangeError(
        "Maximum total wait must be a whole number between 0 and 120 minutes.",
      );
      setQueueInvalidFields(["maximumWait"]);
      return;
    }
    const saveScope = facilityScopeRef.current;
    setSavingQueue(true);
    try {
      const saved = await setQueueConfig(saveScope.facilityId, {
        queue_assignment_mode: queueAssignmentMode,
        queue_session_duration_minutes: queueSessionDuration,
        queue_minimum_remaining_minutes: queueMinimumRemaining,
        queue_auto_court_strategy: queueAutoCourtStrategy,
        queue_auto_target_occupancy: queueAutoTargetOccupancy,
        queue_auto_minimum_starting_occupancy:
          queueAutoMinimumStartingOccupancy,
        queue_auto_late_join_timing_policy: queueAutoLateJoinTimingPolicy,
        queue_auto_max_wait_minutes: queueAutoMaxWait,
      });
      const queueConfig = {
        queue_assignment_mode: saved.queue_assignment_mode,
        queue_session_duration_minutes: saved.queue_session_duration_minutes,
        queue_minimum_remaining_minutes: saved.queue_minimum_remaining_minutes,
        queue_auto_court_strategy: saved.queue_auto_court_strategy,
        queue_auto_target_occupancy: saved.queue_auto_target_occupancy,
        queue_auto_minimum_starting_occupancy:
          saved.queue_auto_minimum_starting_occupancy,
        queue_auto_late_join_timing_policy:
          saved.queue_auto_late_join_timing_policy,
        queue_auto_max_wait_minutes: saved.queue_auto_max_wait_minutes,
      };
      if (!isCurrentScope(saveScope)) return;
      queryClient.setQueriesData<FacilityListItem[]>(
        { queryKey: ["org-facilities"] },
        (facilities) =>
          facilities?.map((facility) =>
            facility.facility_id === saveScope.facilityId
              ? { ...facility, ...queueConfig }
              : facility,
          ),
      );
      queryClient.setQueryData<FacilityListItem | FacilityDetail>(
        ["facility", saveScope.facilityId],
        (facility) => (facility ? { ...facility, ...queueConfig } : saved),
      );
      setQueueAssignmentMode(queueConfig.queue_assignment_mode);
      setQueueSessionDuration(queueConfig.queue_session_duration_minutes);
      setQueueMinimumRemaining(queueConfig.queue_minimum_remaining_minutes);
      setQueueAutoCourtStrategy(queueConfig.queue_auto_court_strategy);
      setQueueAutoTargetOccupancy(queueConfig.queue_auto_target_occupancy);
      setQueueAutoMinimumStartingOccupancy(
        queueConfig.queue_auto_minimum_starting_occupancy,
      );
      setQueueAutoLateJoinTimingPolicy(
        queueConfig.queue_auto_late_join_timing_policy,
      );
      setQueueAutoMaxWait(queueConfig.queue_auto_max_wait_minutes);
      setQueueDirty(false);
      setQueueError(null);
      setQueueNotice("Drop-in queue settings saved.");
    } catch (err) {
      if (!isCurrentScope(saveScope)) return;
      setQueueError(
        err instanceof Error
          ? err.message
          : "Failed to save drop-in queue settings",
      );
    } finally {
      if (isCurrentScope(saveScope)) setSavingQueue(false);
    }
  };

  const updateRecoveryReason = (
    index: number,
    update: (reason: QueueRecoveryReasonInput) => QueueRecoveryReasonInput,
  ) => {
    setRecoveryReasons((reasons) =>
      reasons.map((reason, reasonIndex) =>
        reasonIndex === index ? update(reason) : reason,
      ),
    );
    setRecoveryDirty(true);
    setRecoveryError(null);
    setRecoveryNotice(null);
  };

  const saveRecoveryReasons = async () => {
    if (!recoveryReasonsLoaded || !recoveryDirty || savingRecovery) return;
    const labels = recoveryReasons.map((reason) => reason.label.trim());
    if (
      labels.length === 0 ||
      labels.some((label) => label.length === 0) ||
      new Set(labels.map((label) => label.toLocaleLowerCase())).size !==
        labels.length
    ) {
      setRecoveryError("Use a unique, non-empty label for every reason.");
      return;
    }
    const uncoveredAction = RECOVERY_ACTIONS.find(
      (action) =>
        !recoveryReasons.some((reason) =>
          reason.actions.includes(action.value),
        ),
    );
    if (uncoveredAction) {
      setRecoveryError(
        `At least one reason must support ${uncoveredAction.label.toLocaleLowerCase()}.`,
      );
      return;
    }

    const saveScope = facilityScopeRef.current;
    setSavingRecovery(true);
    setRecoveryError(null);
    setRecoveryNotice(null);
    try {
      const saved = await replaceQueueRecoveryReasons(saveScope.facilityId, {
        reasons: recoveryReasons.map((reason, index) => ({
          ...(reason.reason_id ? { reason_id: reason.reason_id } : {}),
          label: labels[index],
          actions: reason.actions,
        })),
      });
      if (!isCurrentScope(saveScope)) return;
      queryClient.setQueryData<QueueRecoveryReason[]>(
        ["queue-recovery-reasons", saveScope.facilityId],
        saved,
      );
      setRecoveryReasons(editableRecoveryReasons(saved));
      setRecoveryDirty(false);
      setRecoveryNotice("Recovery reasons saved.");
    } catch (err) {
      if (!isCurrentScope(saveScope)) return;
      setRecoveryError(
        err instanceof Error ? err.message : "Failed to save recovery reasons",
      );
    } finally {
      if (isCurrentScope(saveScope)) setSavingRecovery(false);
    }
  };

  if (loading) {
    return (
      <Stack
        aria-label="Loading drop-in settings"
        role="status"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: "30vh",
        }}
      >
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  const checkInEditorDisabled =
    !canManage || savingCheckIn || !facilityDetailLoaded;
  const queueEditorDisabled =
    !canManage || savingQueue || !facilityDetailLoaded || !queuePolicyLoaded;

  return (
    <Stack spacing={3}>
      {error ? <Alert severity="error">{error}</Alert> : null}

      <SettingsSection title="Check-in" titleId="drop-in-check-in-title">
        <Typography color="text.secondary" variant="body2">
          Choose how players check in for drop-in, and whether staff can check
          players in at the front desk.
        </Typography>
        {permissionChecked && !canManage ? (
          <Typography color="text.secondary" variant="body2">
            Only facility managers can change these settings.
          </Typography>
        ) : null}

        <TextField
          disabled={checkInEditorDisabled}
          id="check-in-mode"
          label="Check-in mode"
          onChange={(event) =>
            setCheckInMode(event.target.value as CheckInMode)
          }
          select
          size="small"
          sx={{ maxWidth: 360 }}
          value={checkInMode}
        >
          {SELECTABLE_CHECK_IN_MODES.map((mode) => (
            <MenuItem
              disabled={mode === "staff_check_in" && !staffCheckInAvailable}
              key={mode}
              value={mode}
            >
              {MODE_LABELS[mode]}
            </MenuItem>
          ))}
        </TextField>

        <Paper sx={{ px: 1.5, py: 1 }} variant="outlined">
          <FormControlLabel
            control={
              <Switch
                checked={staffCheckInAvailable}
                disabled={checkInEditorDisabled}
                id="staff-check-in"
                onChange={(_, checked) => onToggleStaff(checked)}
              />
            }
            label="Staff check-in"
            labelPlacement="start"
            sx={{ justifyContent: "space-between", m: 0, width: "100%" }}
          />
          <Typography color="text.secondary" component="p" variant="caption">
            Let staff check players in at the front desk. Required before you
            can select staff check-in above.
          </Typography>
        </Paper>

        {checkInError ? <Alert severity="error">{checkInError}</Alert> : null}

        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
        >
          <Button
            disabled={checkInEditorDisabled || !checkInDirty}
            onClick={() => void saveCheckIn()}
            size="small"
            variant="contained"
          >
            {savingCheckIn ? "Saving…" : "Save check-in"}
          </Button>
          {checkInNotice ? (
            <Typography color="text.secondary" role="status" variant="body2">
              {checkInNotice}
            </Typography>
          ) : null}
        </Stack>
      </SettingsSection>

      <SettingsSection title="Drop-in queue" titleId="drop-in-queue-title">
        <Typography color="text.secondary" variant="body2">
          Control court block timing and how Auto balances active and fresh
          courts from this facility’s shared court inventory.
        </Typography>
        {permissionChecked && !canManage ? (
          <Typography color="text.secondary" variant="body2">
            Only facility managers can change these queue settings.
          </Typography>
        ) : null}

        <TextField
          disabled={queueEditorDisabled}
          id="queue-assignment-mode"
          label="Assignment mode"
          onChange={(event) => {
            setQueueAssignmentMode(
              event.target.value as "manual" | "auto" | "self_serve",
            );
            setQueueDirty(true);
            clearQueueValidation();
          }}
          select
          size="small"
          sx={{ maxWidth: 360 }}
          value={queueAssignmentMode}
        >
          <MenuItem value="auto">Auto — system assigns</MenuItem>
          <MenuItem value="manual">Manual — staff calls players</MenuItem>
          <MenuItem value="self_serve">
            Self-serve — players pick courts
          </MenuItem>
        </TextField>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          <QueuePolicyNumberInput
            disabled={queueEditorDisabled}
            errorId={
              queueInvalidFields.includes("duration")
                ? "queue-range-error"
                : undefined
            }
            id="queue-session-duration"
            label="Court block duration (minutes)"
            max={720}
            min={2}
            onChange={setQueueSessionDuration}
            onDirty={() => {
              setQueueDirty(true);
              clearQueueValidation();
            }}
            value={queueSessionDuration}
          />
          <QueuePolicyNumberInput
            disabled={queueEditorDisabled}
            errorId={
              queueInvalidFields.includes("minimum")
                ? "queue-range-error"
                : undefined
            }
            id="queue-minimum-remaining"
            label="Minimum time to join an active court (minutes)"
            max={719}
            min={1}
            onChange={setQueueMinimumRemaining}
            onDirty={() => {
              setQueueDirty(true);
              clearQueueValidation();
            }}
            value={queueMinimumRemaining}
          />
          <TextField
            disabled={queueEditorDisabled || queueAssignmentMode !== "auto"}
            fullWidth
            id="queue-auto-strategy"
            label="Auto court strategy"
            onChange={(event) => {
              setQueueAutoCourtStrategy(
                event.target.value as QueueAutoCourtStrategy,
              );
              setQueueDirty(true);
              clearQueueValidation();
            }}
            select
            size="small"
            value={queueAutoCourtStrategy}
          >
            {Object.entries(AUTO_STRATEGY_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <QueuePolicyNumberInput
            disabled={queueEditorDisabled || queueAssignmentMode !== "auto"}
            errorId={
              queueInvalidFields.includes("target")
                ? "queue-range-error"
                : undefined
            }
            id="queue-auto-target-occupancy"
            label="Target occupancy"
            max={6}
            min={2}
            onChange={setQueueAutoTargetOccupancy}
            onDirty={() => {
              setQueueDirty(true);
              clearQueueValidation();
            }}
            value={queueAutoTargetOccupancy}
          />
          <QueuePolicyNumberInput
            disabled={queueEditorDisabled || queueAssignmentMode !== "auto"}
            errorId={
              queueInvalidFields.includes("minimumStarting")
                ? "queue-range-error"
                : undefined
            }
            id="queue-auto-minimum-starting-occupancy"
            label="Minimum players to open a court"
            max={6}
            min={1}
            onChange={setQueueAutoMinimumStartingOccupancy}
            onDirty={() => {
              setQueueDirty(true);
              clearQueueValidation();
            }}
            value={queueAutoMinimumStartingOccupancy}
          />
          <TextField
            disabled={queueEditorDisabled || queueAssignmentMode !== "auto"}
            fullWidth
            id="queue-auto-late-join-timing"
            label="Late-join court time"
            onChange={(event) => {
              setQueueAutoLateJoinTimingPolicy(
                event.target.value as QueueAutoLateJoinTimingPolicy,
              );
              setQueueDirty(true);
              clearQueueValidation();
            }}
            select
            size="small"
            value={queueAutoLateJoinTimingPolicy}
          >
            {Object.entries(AUTO_LATE_JOIN_TIMING_LABELS).map(
              ([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ),
            )}
          </TextField>
          <QueuePolicyNumberInput
            disabled={queueEditorDisabled || queueAssignmentMode !== "auto"}
            errorId={
              queueInvalidFields.includes("maximumWait")
                ? "queue-range-error"
                : undefined
            }
            help="Applied once a playable cohort exists; time already waited counts toward the limit."
            id="queue-auto-max-wait"
            label="Maximum total wait for the oldest compatible player (minutes)"
            max={120}
            min={0}
            onChange={setQueueAutoMaxWait}
            onDirty={() => {
              setQueueDirty(true);
              clearQueueValidation();
            }}
            value={queueAutoMaxWait}
          />
        </Box>

        {queueRangeError ? (
          <Alert id="queue-range-error" severity="error">
            {queueRangeError}
          </Alert>
        ) : null}
        {queueError ? <Alert severity="error">{queueError}</Alert> : null}
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
        >
          <Button
            disabled={queueEditorDisabled || !queueDirty}
            onClick={() => void saveQueueConfig()}
            size="small"
            variant="contained"
          >
            {savingQueue ? "Saving…" : "Save queue settings"}
          </Button>
          {queueNotice ? (
            <Typography color="text.secondary" role="status" variant="body2">
              {queueNotice}
            </Typography>
          ) : null}
        </Stack>

        <Divider />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ alignItems: { xs: "stretch", sm: "flex-start" } }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography component="h3" sx={{ fontWeight: 700 }} variant="body2">
              Recovery reasons
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mt: 0.5 }}
              variant="caption"
            >
              Staff must choose one of these reasons when skipping, removing,
              ending, or moving a party.
            </Typography>
          </Box>
          <Button
            disabled={
              !canManage ||
              !recoveryReasonsLoaded ||
              savingRecovery ||
              recoveryReasons.length >= 20
            }
            onClick={() => {
              setRecoveryReasons((reasons) => [
                ...reasons,
                { label: "", actions: [] },
              ]);
              setRecoveryDirty(true);
              setRecoveryError(null);
              setRecoveryNotice(null);
            }}
            size="small"
            startIcon={<AddIcon />}
            type="button"
            variant="outlined"
          >
            Add reason
          </Button>
        </Stack>

        <Stack spacing={1.25}>
          <Box
            aria-hidden
            sx={{
              color: "text.secondary",
              display: { xs: "none", xl: "grid" },
              fontSize: "0.75rem",
              fontWeight: 700,
              gap: 1,
              gridTemplateColumns: "minmax(12rem, 1fr) repeat(5, 6rem) 2.5rem",
              px: 1,
            }}
          >
            <span>Reason</span>
            {RECOVERY_ACTIONS.map((action) => (
              <Box
                component="span"
                key={action.value}
                sx={{ textAlign: "center" }}
              >
                {action.label}
              </Box>
            ))}
            <span />
          </Box>

          {recoveryReasons.map((reason, index) => (
            <Paper
              data-layout-breakpoint="xl"
              data-recovery-row
              key={reason.reason_id ?? `new-${index}`}
              sx={{
                alignItems: { xl: "center" },
                display: "grid",
                gap: { xs: 1.5, xl: 1 },
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  xl: "minmax(12rem, 1fr) repeat(5, 6rem) 2.5rem",
                },
                p: { xs: 1.5, xl: 1 },
              }}
              variant="outlined"
            >
              <TextField
                disabled={
                  !canManage || !recoveryReasonsLoaded || savingRecovery
                }
                fullWidth
                onChange={(event) =>
                  updateRecoveryReason(index, (current) => ({
                    ...current,
                    label: event.target.value,
                  }))
                }
                placeholder="Reason label"
                size="small"
                slotProps={{
                  htmlInput: {
                    "aria-label": `Recovery reason ${index + 1}`,
                    maxLength: 80,
                  },
                }}
                value={reason.label}
              />
              <Box
                sx={{
                  display: { xs: "grid", xl: "contents" },
                  gap: 1,
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                }}
              >
                {RECOVERY_ACTIONS.map((action) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={reason.actions.includes(action.value)}
                        disabled={
                          !canManage || !recoveryReasonsLoaded || savingRecovery
                        }
                        onChange={(_, checked) =>
                          updateRecoveryReason(index, (current) => ({
                            ...current,
                            actions: checked
                              ? [...current.actions, action.value]
                              : current.actions.filter(
                                  (value) => value !== action.value,
                                ),
                          }))
                        }
                        size="small"
                        slotProps={{
                          input: {
                            "aria-label": `${reason.label || `Reason ${index + 1}`} applies to ${action.label}`,
                          },
                        }}
                      />
                    }
                    key={action.value}
                    label={action.label}
                    sx={{
                      color: "text.secondary",
                      justifyContent: { xs: "flex-start", xl: "center" },
                      m: 0,
                      "& .MuiFormControlLabel-label": {
                        display: { xs: "inline", xl: "none" },
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                ))}
              </Box>
              <IconButton
                aria-label={`Delete ${reason.label || `reason ${index + 1}`}`}
                color="error"
                disabled={
                  !canManage ||
                  !recoveryReasonsLoaded ||
                  savingRecovery ||
                  recoveryReasons.length <= 1
                }
                onClick={() => {
                  setRecoveryReasons((reasons) =>
                    reasons.filter((_, reasonIndex) => reasonIndex !== index),
                  );
                  setRecoveryDirty(true);
                  setRecoveryError(null);
                  setRecoveryNotice(null);
                }}
                size="small"
                title="Delete reason"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Stack>

        {recoveryError ? <Alert severity="error">{recoveryError}</Alert> : null}
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
        >
          <Button
            disabled={
              !canManage ||
              !recoveryReasonsLoaded ||
              savingRecovery ||
              !recoveryDirty
            }
            onClick={() => void saveRecoveryReasons()}
            size="small"
            variant="contained"
          >
            {savingRecovery ? "Saving…" : "Save recovery reasons"}
          </Button>
          {recoveryNotice ? (
            <Typography color="text.secondary" role="status" variant="body2">
              {recoveryNotice}
            </Typography>
          ) : null}
        </Stack>
      </SettingsSection>

      <OpenPlayCancellationPolicyCard
        canManage={canManage}
        facilityId={facilityId}
        permissionChecked={permissionChecked}
      />

      <SettingsSection title="Drop-in pricing" titleId="drop-in-pricing-title">
        <Typography color="text.secondary" variant="body2">
          Set the per-session price players pay to drop in. The days and hours
          a drop-in rate covers also define when drop-in is open.
        </Typography>
        {pricingError ? <Alert severity="error">{pricingError}</Alert> : null}
        <SimpleRatesMode
          config={OPEN_PLAY_SIMPLE_CONFIG}
          currency="USD"
          disabled={!pricingLoaded}
          facilityId={facilityId}
          onSaved={refreshRules}
          rules={openPlayRules}
        />
        <Typography color="text.secondary" variant="caption">
          Need peak windows, member rates, or per-plan overrides?{" "}
          <MuiLink component={RouterLink} to="/settings/rates">
            Open the full rates editor
          </MuiLink>
          .
        </Typography>
      </SettingsSection>
    </Stack>
  );
}
